<template>
  <div class="min-h-screen bg-gray-50">
    <div class="container mx-auto px-4 py-8 max-w-7xl">
      <!-- Error State -->
      <div
        v-if="error"
        class="bg-red-50 border border-red-200 rounded-xl p-8 text-center"
      >
        <svg
          class="w-16 h-16 text-red-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 class="text-lg font-semibold text-red-800 mb-2">
          Error Loading Ticket
        </h3>
        <p class="text-red-600">{{ error }}</p>
        <button
          @click="fetchTicket"
          class="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Try Again
        </button>
      </div>

      <!-- Ticket Content -->
      <div v-else-if="ticket" class="space-y-6">
        <!-- Header with Navigation -->
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <button
              @click="goBack"
              class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg
                class="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <h1 class="text-2xl font-bold text-gray-900">Ticket Details</h1>
          </div>

          <div class="flex items-center space-x-3">
            <span class="text-sm text-gray-500"
              >Ticket #{{ ticket.ticket_number }}</span
            >
            <StatusBadge :status="String(ticket.decision)" />
          </div>
        </div>

        <!-- Main Grid -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- Left Column - Customer Info & Documents -->
          <div class="lg:col-span-2 space-y-6">
            <!-- Customer Information Card -->
            <div
              class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <div
                class="px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-100"
              >
                <h2
                  class="text-lg font-semibold text-gray-900 flex items-center"
                >
                  <svg
                    class="w-5 h-5 mr-2 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  Client Information
                </h2>
              </div>
              <div class="p-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      class="text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >Full Name</label
                    >
                    <p class="mt-1 text-lg font-medium text-gray-900">
                      {{ ticket.client?.full_name }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >Gender</label
                    >
                    <p class="mt-1 text-gray-700">
                      {{ ticket.client?.gender }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >ID Number</label
                    >
                    <p class="mt-1 text-gray-700">
                      {{ ticket.client?.id_number }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >Date of Birth</label
                    >
                    <p class="mt-1 text-gray-700">
                      {{ formatDateOfBirth(ticket.client?.dob) }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >Education</label
                    >
                    <p class="mt-1 text-gray-700">
                      {{ ticket.client?.education }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >Work Status</label
                    >
                    <p class="mt-1 text-gray-700">
                      {{ ticket.client?.work_status }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >Income Band</label
                    >
                    <p class="mt-1 text-gray-700">
                      {{ ticket.client?.income_band }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >Phone Number</label
                    >
                    <p class="mt-1 text-gray-700 flex items-center">
                      {{ formatKenyanPhone(ticket.client?.phone) }}
                      <button
                        @click="
                          copyToClipboard(
                            formatKenyanPhone(ticket.client?.phone)
                          )
                        "
                        class="ml-2 p-1 hover:bg-gray-100 rounded"
                        title="Copy phone number"
                      >
                        <svg
                          class="w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      </button>
                    </p>
                  </div>
                  <div>
                    <label
                      class="text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >Alternative Phone Numbers</label
                    >
                    <div class="mt-1 flex items-center gap-2">
                      <StatusBadge :status="altPhoneStatusLabel" />
                    </div>
                    <div
                      v-if="ticket.client?.alt_phones && ticket.client.alt_phones.length > 0"
                      class="mt-1 space-y-1"
                    >
                      <div
                        v-for="altPhone in ticket.client.alt_phones"
                        :key="altPhone.id"
                        class="text-gray-700 flex items-center"
                      >
                        {{ formatKenyanPhone(altPhone.phone) }}
                        <button
                          @click="copyToClipboard(formatKenyanPhone(altPhone.phone))"
                          class="ml-2 p-1 hover:bg-gray-100 rounded"
                          title="Copy phone number"
                        >
                          <svg
                            class="w-4 h-4 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <p v-else class="mt-1 text-gray-400 italic">No alternative numbers</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Documents Card -->
            <div
              class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <div
                class="px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-100"
              >
                <h2
                  class="text-lg font-semibold text-gray-900 flex items-center"
                >
                  <svg
                    class="w-5 h-5 mr-2 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Documents
                </h2>
              </div>
              <div class="p-6">
                <div
                  v-if="ticket.documents && ticket.documents.length > 0"
                  class="grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                  <div
                    v-for="doc in ticket.documents"
                    @click="openModal(doc.data)"
                    :key="doc.id"
                    class="flex cursor-pointer items-center p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all group"
                  >
                    <div
                      class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3 group-hover:bg-blue-100"
                    >
                      <svg
                        class="w-5 h-5 text-gray-500 group-hover:text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <div class="flex-1">
                      <p class="text-sm font-medium text-gray-900">
                        {{ doc.name }}
                      </p>
                      <div class="flex items-center">
                        <span class="text-xs font-medium text-gray-900">
                          Click to view
                        </span>
                      </div>
                    </div>
                    <StatusBadge :status="getDocumentStatusLabel(doc.ctype)" />
                  </div>
                </div>
                <div v-else class="text-center py-8 text-gray-500">
                  <svg
                    class="w-12 h-12 text-gray-300 mx-auto mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p>No documents uploaded</p>
                </div>
              </div>
            </div>

            <!-- Call History Card -->
            <div
              class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <div
                class="px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-100"
              >
                <div class="flex items-center justify-between">
                  <h2
                    class="text-lg font-semibold text-gray-900 flex items-center"
                  >
                    <svg
                      class="w-5 h-5 mr-2 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    Call History
                  </h2>
                  <span class="text-sm text-gray-500"
                    >{{ ticket.call_attempts?.length || 0 }} attempts</span
                  >
                </div>
              </div>
              <div class="p-6">
                <div
                  v-if="ticket.call_attempts && ticket.call_attempts.length > 0"
                  class="space-y-4"
                >
                  <div
                    v-for="attempt in sortedCallAttempts"
                    :key="attempt.id"
                    class="relative pl-8 pb-4 border-l-2 border-gray-200 last:border-0 last:pb-0"
                  >
                    <!-- Timeline dot -->
                    <div
                      class="absolute left-[-9px] w-4 h-4 rounded-full border-2 border-white"
                      :class="outcomeColors[attempt.summary]"
                    ></div>

                    <!-- Attempt content -->
                    <div
                      class="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                    >
                      <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center space-x-2">
                          <span class="text-sm font-medium text-gray-900"
                            >Attempt #{{ attempt.attempt_count }}</span
                          >
                          <OutcomeBadge :outcome="attempt.summary" />
                        </div>
                        <span class="text-xs text-gray-500">{{
                          formatDateTime(attempt.created_at)
                        }}</span>
                      </div>

                      <div class="grid grid-cols-2 gap-2 text-sm mb-2">
                        <div>
                          <span class="text-gray-500">Contact:</span>
                          <span class="ml-2 text-gray-900">{{
                            attempt.contact_name
                          }}</span>
                        </div>
                        <div>
                          <span class="text-gray-500">Phone:</span>
                          <span class="ml-2 text-gray-900">{{
                            attempt.phone_number
                          }}</span>
                        </div>
                      </div>

                      <p
                        v-if="attempt.notes"
                        class="text-sm text-gray-600 mt-2 bg-white p-2 rounded"
                      >
                        {{ attempt.notes }}
                      </p>

                      <!-- Next step if any -->
                      <div
                        v-if="attempt.nextStep"
                        class="mt-2 flex items-center space-x-2"
                      >
                        <span
                          class="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full"
                        >
                          Next: {{ attempt.nextStep }}
                        </span>
                        <span
                          v-if="attempt.callbackTime"
                          class="text-xs text-gray-500"
                        >
                          at {{ formatTime(attempt.callbackTime) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-center py-8 text-gray-500">
                  <svg
                    class="w-12 h-12 text-gray-300 mx-auto mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <p>No call attempts yet</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column - Verification Flow, Actions, Contacts, Status -->
          <div class="space-y-6">
            <!-- Verification Flow Card (was "Actions") -->
            <div
              class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <div
                class="px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-100"
              >
                <h2
                  class="text-lg font-semibold text-gray-900 flex items-center"
                >
                  <svg
                    class="w-5 h-5 mr-2 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Verification Flow
                </h2>
              </div>
              <div class="p-6 space-y-3">
                <button
                  v-for="(stage, index) in verificationStages"
                  :key="stage.key"
                  @click="goToVerificationStage(stage.route)"
                  class="w-full flex items-center gap-3 border border-gray-200 rounded-lg px-4 py-3 hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
                >
                  <div
                    class="shrink-0 w-7 h-7 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold flex items-center justify-center"
                  >
                    {{ index + 1 }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-gray-900">{{ stage.label }}</p>
                    <p
                      v-if="stage.key !== 'contact_verifications' && getLatestVerification(stage.key)?.summary"
                      class="text-xs text-gray-500 mt-0.5 truncate"
                    >
                      {{ getLatestVerification(stage.key).summary }}
                    </p>
                  </div>
                  <div class="flex items-center gap-2 shrink-0">
                    <StatusBadge
                      :status="stage.key === 'contact_verifications' ? getContactStageLabel() : getVerificationLabel(stage.key)"
                    />
                    <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              </div>
            </div>

            <!-- Emergency Contacts Card -->
            <div
              class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <div
                class="px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-100"
              >
                <h2
                  class="text-lg font-semibold text-gray-900 flex items-center"
                >
                  <svg
                    class="w-5 h-5 mr-2 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  Emergency Contacts
                </h2>
              </div>
              <div class="p-6">
                <div
                  v-if="
                    ticket.emergencyContacts &&
                    ticket.emergencyContacts.length > 0
                  "
                  class="space-y-4"
                >
                  <div
                    v-for="contact in ticket.emergencyContacts"
                    :key="contact.id"
                    class="border border-gray-200 rounded-lg px-4 py-2 hover:border-blue-300 transition-colors"
                  >
                    <div class="flex items-center justify-between mb-0">
                      <div class="flex items-center">
                        <span class="font-medium text-gray-900">{{
                          contact.name
                        }}</span>
                      </div>
                      <div class="flex flex-col items-center">
                        <span class="text-xs text-gray-500">{{
                          contact.relationship
                        }}</span>
                        <StatusBadge
                          class="mt-2"
                          :status="getEmergencyContactStatusLabel(contact.id)"
                        />
                      </div>
                    </div>
                    <p class="text-sm text-gray-600 flex items-center">
                      <svg
                        class="w-4 h-4 mr-1 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      {{ contact.phone }}
                    </p>
                  </div>
                </div>
                <div v-else class="text-center py-6 text-gray-500">
                  No emergency contacts listed
                </div>
              </div>
            </div>

            <!-- Status Card -->
            <div
              class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <div
                class="px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-100"
              >
                <h2
                  class="text-lg font-semibold text-gray-900 flex items-center"
                >
                  <svg
                    class="w-5 h-5 mr-2 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Status
                </h2>
              </div>
              <div class="p-6 space-y-4">
                <!-- Current Status -->
                <div>
                  <label
                    class="text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >Current Status</label
                  >
                  <div class="mt-2">
                    <StatusBadge :status="String(ticket.decision)" size="lg" />
                  </div>
                </div>

                <!-- Dates -->
                <div class="pt-4 border-t border-gray-100">
                  <div class="flex justify-between text-sm">
                    <span class="text-gray-500">Created:</span>
                    <span class="text-gray-900">{{
                      formatDate(ticket.created_at)
                    }}</span>
                  </div>
                  <div class="flex justify-between text-sm mt-2">
                    <span class="text-gray-500">Last Updated:</span>
                    <span class="text-gray-900">{{
                      formatDate(ticket.updated_at)
                    }}</span>
                  </div>
                  <div
                    v-if="ticket.completedAt"
                    class="flex justify-between text-sm mt-2"
                  >
                    <span class="text-gray-500">Completed:</span>
                    <span class="text-gray-900">{{
                      formatDate(ticket.completedAt)
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <!-- Modal Overlay -->
        <Transition name="modal-fade">
          <div
            v-if="isModalOpen"
            class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            @click.self="closeModal"
          >
            <!-- Modal Content -->
            <div
              class="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
            >
              <!-- Modal Header -->
              <div class="flex justify-between items-center p-4 border-b">
                <h3 class="text-xl font-semibold text-gray-800">
                  Document Preview
                </h3>
                <button
                  @click="closeModal"
                  class="text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  <svg
                    class="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                </button>
              </div>

              <!-- Modal Body - Image Display -->
              <div class="p-6 overflow-auto max-h-[calc(90vh-120px)]">
                <img
                  :src="base64Image"
                  alt="Base64 Image"
                  class="max-w-full h-auto rounded-lg shadow-md mx-auto"
                />
              </div>

              <!-- Modal Footer with Close Button -->
              <div class="flex justify-end p-4 border-t bg-gray-50">
                <button
                  @click="closeModal"
                  class="px-6 py-2 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </div>

      <!-- Call Attempt Modal -->
      <div v-if="showCallModal" class="fixed inset-0 z-50 overflow-y-auto">
        <div
          class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            @click="closeCallModal"
          ></div>

          <div
            class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          >
            <div class="bg-white px-6 pt-5 pb-4 sm:p-6">
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-lg font-semibold text-gray-900">
                  Log Call Attempt - {{ ticket.ticket_number }}
                </h3>
                <button
                  @click="closeCallModal"
                  class="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    class="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <form @submit.prevent="submitCallAttempt">
                <!-- Contact Type -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >Contact Type</label
                  >
                  <div class="flex space-x-4">
                    <label class="flex items-center">
                      <input
                        type="radio"
                        v-model="callForm.contact_type"
                        value="Client"
                        class="mr-2"
                      />
                      <span class="text-sm text-gray-700"
                        >Alternative Phone Number</span
                      >
                    </label>
                    <label class="flex items-center">
                      <input
                        type="radio"
                        v-model="callForm.contact_type"
                        value="Emergency"
                        class="mr-2"
                      />
                      <span class="text-sm text-gray-700"
                        >Emergency Contact</span
                      >
                    </label>
                  </div>
                </div>

                <!-- Emergency Contact Selection -->
                <div v-if="callForm.contact_type === 'Emergency'" class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >Select Emergency Contact</label
                  >
                  <select
                    v-model="callForm.contact_id"
                    required
                    class="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose a contact</option>
                    <option
                      v-for="contact in ticket?.emergency_contacts"
                      :key="contact.id"
                      :value="contact.id"
                    >
                      {{ contact.name }} ({{ contact.relationship }}) -
                      {{ contact.phone }}
                    </option>
                  </select>
                </div>

                <!-- Phone Number -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >Phone Number</label
                  >
                  <input
                    v-model="callForm.phone_number"
                    type="tel"
                    required
                    class="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter phone number"
                  />
                </div>

                <!-- Contact Name -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >Contact Person Name</label
                  >
                  <input
                    v-model="callForm.contact_name"
                    type="text"
                    class="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Name of person spoken to"
                  />
                </div>

                <!-- Outcome -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >Outcome</label
                  >
                  <select
                    v-model="callForm.decision"
                    required
                    class="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    @change="handleOutcomeChange"
                  >
                    <option value="">Select outcome</option>
                    <option value="Verified">Confirmed - Successful</option>
                    <option value="Rejected">Not Confirmed - Failed</option>
                  </select>
                </div>

                <!-- Notes -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >Notes</label
                  >
                  <textarea
                    v-model="callForm.summary"
                    rows="3"
                    class="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Add any notes about the call..."
                  ></textarea>
                </div>

                <!-- Submit Buttons -->
                <div class="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    @click="closeCallModal"
                    class="px-4 py-2 border border-red-400 text-red-400 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    class="px-4 py-2 bg-indigo-800 text-white rounded-lg hover:bg-indigo-700"
                    :disabled="isSubmitting"
                  >
                    {{ isSubmitting ? "Logging..." : "Log Call Attempt" }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <!-- Success Notification -->
      <div
        v-if="showNotification"
        class="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3 animate-slide-up z-50"
      >
        <svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>{{ notificationMessage }}</span>
      </div>
    </div>

    <!-- Floating Action Button -->
    <button
      @click="fetchTicket"
      class="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-indigo-800 text-white shadow-lg transition-all duration-300 hover:bg-indigo-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      aria-label="Refresh page"
    >
      <!-- Refresh icon (SVG) -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        :class="{ 'animate-spin': isRefreshing }"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    </button>

    <div
      v-if="isRefreshing"
      class="fixed inset-0 ml-64 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50"
    >
      <div class="relative">
        <!-- Outer ring -->
        <div class="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
        <!-- Inner spinning gradient ring -->
        <div
          class="w-16 h-16 border-4 border-transparent rounded-full absolute top-0 left-0 border-t-indigo-500 border-r-indigo-700 animate-spin"
        ></div>
        <!-- Center dot -->
        <div
          class="w-3 h-3 bg-indigo-800 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import StatusBadge from "@/components/ui/StatusBadge.vue";
import OutcomeBadge from "@/components/ui/OutcomeBadge.vue";

import api from "@/utils/api";

import { useNotifications } from "@/composables/useNotifications";

// Notification system
const { showError, formatValidationErrors } = useNotifications();

const route = useRoute();
const router = useRouter();

// State
const ticket = ref(null);
const isRefreshing = ref(false);
const error = ref(null);
const isSubmitting = ref(false);
const showNotification = ref(false);
const notificationMessage = ref("");

// Modal states
const showCallModal = ref(false);
const showNextSteps = ref(false);

// Form states
const callForm = ref({
  contact_type: "",
  contact_id: "",
  phone_number: "",
  contact_name: "",
  decision: "",
  summary: "",
});

// Outcome colors
const outcomeColors = {
  reached: "bg-green-500",
  confirmed: "bg-green-500",
  no_answer: "bg-yellow-500",
  busy: "bg-yellow-500",
  wrong_number: "bg-red-500",
  not_reachable: "bg-red-500",
  call_back_later: "bg-blue-500",
  denied_relationship: "bg-orange-500",
  language_barrier: "bg-purple-500",
  deceased: "bg-gray-500",
  other: "bg-gray-400",
};

// Verification stages — order: Credit -> SMS -> KYC -> Contact -> Final
// Each record's `decision_label` field is computed on the backend
// (App\Models\*::getDecisionLabelAttribute) — no client-side mapping needed.
const verificationStages = [
  { key: "credit_verifications", label: "Credit", route: "CreditVerification" },
  { key: "sms_verifications", label: "SMS", route: "TicketSMSView" },
  { key: "kyc_verifications", label: "KYC", route: "KycVerification" },
  { key: "contact_verifications", label: "Contact", route: "ContactVerification" },
  { key: "final_verifications", label: "Final", route: "FinalVerification" },
];

const goToVerificationStage = (routeName) => {
  router.push({
    name: routeName,
    params: { id: route.params.id },
    query: { client_id: ticket.value?.client_id },
  });
};

// Backend returns each verification relation ordered latest()->get(),
// so index 0 is always the most recent record for that stage
const getLatestVerification = (key) => {
  const records = ticket.value?.[key];
  return records && records.length ? records[0] : null;
};

const getVerificationLabel = (key) => {
  const latest = getLatestVerification(key);
  return latest?.decision_label ?? "Pending";
};

// ---------------------------------------------------------------------
// KycVerification-driven document badges. ctype values match
// App\Models\KycVerification constants: 1 = ID Front, 2 = ID Back,
// 3 = Selfie, 4 = Emergency Contact. Labels come straight from the
// backend's decision_label accessor.
// ---------------------------------------------------------------------

const getKycVerifications = () => ticket.value?.kyc_verifications || [];

const getLatestKycByCtype = (ctype) => {
  const records = getKycVerifications().filter((r) => r.ctype === ctype);
  if (!records.length) return null;
  return records.sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  )[0];
};

const getDocumentStatusLabel = (ctype) => {
  const latest = getLatestKycByCtype(ctype);
  return latest?.decision_label ?? "Pending";
};

// ---------------------------------------------------------------------
// ContactVerification-driven badges: "self" (alt phone) and each
// individual emergency contact. ctype values (1 = self, 2 = emergency
// contact) match App\Models\ContactVerification constants. Labels come
// straight from the backend's decision_label accessor.
// ---------------------------------------------------------------------

const CONTACT_TYPE_SELF = 1;
const CONTACT_TYPE_EMERGENCY_CONTACT = 2;

const getContactVerifications = () => ticket.value?.contact_verifications || [];

const getLatestByCtype = (ctype, contactId = null) => {
  const records = getContactVerifications().filter((r) => {
    if (r.ctype !== ctype) return false;
    if (contactId !== null && r.contact_id !== contactId) return false;
    return true;
  });
  if (!records.length) return null;
  return records.sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  )[0];
};

const altPhoneStatusLabel = computed(() => {
  const latest = getLatestByCtype(CONTACT_TYPE_SELF);
  return latest?.decision_label ?? "Pending";
});

const getEmergencyContactStatusLabel = (contactId) => {
  const latest = getLatestByCtype(CONTACT_TYPE_EMERGENCY_CONTACT, contactId);
  return latest?.decision_label ?? "Pending";
};

// Aggregate label for the Contact stage row on the Verification Flow
// card — distinct from getVerificationLabel since contact_verifications
// covers multiple sub-items (self + each emergency contact), not one record.
const getContactStageLabel = () => {
  const records = getContactVerifications();
  if (!records.length) return "Pending";
  if (records.some((r) => r.decision === 0)) return "Rejected";

  const contactCount = ticket.value?.emergencyContacts?.length || 0;
  const hasSelf = records.some((r) => r.ctype === CONTACT_TYPE_SELF);
  const approvedEmergencyCount = new Set(
    records
      .filter((r) => r.ctype === CONTACT_TYPE_EMERGENCY_CONTACT && r.decision === 1)
      .map((r) => r.contact_id)
  ).size;

  if (hasSelf && approvedEmergencyCount >= contactCount) return "Approved";
  return "Pending";
};

// Sorted call attempts
const sortedCallAttempts = computed(() => {
  if (!ticket.value?.call_attempts) return [];
  return [...ticket.value.call_attempts].sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
  );
});

const formatKenyanPhone = (phoneNumber) => {
  if (!phoneNumber) return "N/A";

  try {
    const cleaned = String(phoneNumber).replace(/\D/g, "");

    if (cleaned.startsWith("254")) {
      const localNumber = cleaned.substring(3);

      if (localNumber.length === 9) {
        return "0" + localNumber;
      } else if (cleaned.length === 12) {
        return "0" + cleaned.slice(-9);
      }
    }

    return cleaned;
  } catch (error) {
    console.log(error);
    return "N/A";
  }
};

const formatDateOfBirth = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const day = date.getDate();
    const suffix =
      day >= 11 && day <= 13
        ? "th"
        : ["st", "nd", "rd"][(day % 10) - 1] || "th";

    return `${day}${suffix} ${months[date.getMonth()]} ${date.getFullYear()}`;
  } catch (error) {
    console.log(error);
    return "N/A";
  }
};

// Resolve a stored file path/URL/base64 string into something an <img> can load
const resolveDocumentSrc = (data) => {
  if (!data) return "";
  if (data.startsWith("data:image")) return data;
  if (data.startsWith("http://") || data.startsWith("https://")) return data;
  const base = (api.defaults.baseURL || "").replace(/\/$/, "");
  return `${base}${data.startsWith("/") ? "" : "/"}${data}`;
};

// Fetch ticket data
const fetchTicket = async () => {
  isRefreshing.value = true;
  try {
    const response = await api.get(`/api/v1/tickets/${route.params.id}`);
    ticket.value = response.data || [];

    const clientDoc = ticket.value.client?.client_document;
    // ctype values match App\Models\KycVerification constants:
    // 1 = ID Front, 2 = ID Back, 3 = Selfie
    const documents = [
      { id: 1, ctype: 3, name: "Selfie photo", size: "0MB", data: clientDoc?.selfie, url: "#" },
      { id: 2, ctype: 1, name: "ID Front", size: "0MB", data: clientDoc?.id_front, url: "#" },
      { id: 3, ctype: 2, name: "ID back", size: "0MB", data: clientDoc?.id_back, url: "#" },
    ];

    // Looped Data
    const emergencyContacts = ticket.value.emergency_contacts || [];

    // Update State
    ticket.value.documents = documents;
    ticket.value.emergencyContacts = emergencyContacts;
  } catch (error) {
    console.log(error);
    const details = formatValidationErrors(error);
    showError("Error", "Failed to load tickets", details, 5000);
  } finally {
    isRefreshing.value = false;
  }
};

// Navigation
const goBack = () => {
  router.push("/admin/tickets");
};

// Helper functions
const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
  showNotificationMessage("Phone number copied to clipboard");
};

// View Document handlers
const isModalOpen = ref(false);
const base64Image = ref("");

const openModal = (base64ImageData) => {
  base64Image.value = resolveDocumentSrc(base64ImageData);
  isModalOpen.value = true;
  document.body.style.overflow = "hidden";
};

const closeModal = () => {
  isModalOpen.value = false;
  document.body.style.overflow = "auto";
};


const closeCallModal = () => {
  showCallModal.value = false;
};

const handleOutcomeChange = () => {
  showNextSteps.value = ["no_answer", "busy", "call_back_later"].includes(
    callForm.value.outcome
  );
  if (!showNextSteps.value) {
    callForm.value.nextStep = "";
    callForm.value.callbackTime = "";
  }
};

const submitCallAttempt = async () => {
  isSubmitting.value = true;

  try {
    if (callForm.value.contact_type === "Emergency") {
      const contact = ticket.value.emergencyContacts.find(
        (c) => c.id === parseInt(callForm.value.contact_id)
      );
      callForm.value.contact_name = contact?.name || "";
      callForm.value.emergency_contact_id = contact?.id || null;
    }

    callForm.value.ticket_id = ticket.value.id;
    const response = await api.post("/api/v1/call-attempts", callForm.value);
    console.log(response);

    closeCallModal();
    showNotificationMessage("Call attempt saved successfully");
    fetchTicket();
  } catch (error) {
    console.log(error);
    const details = formatValidationErrors(error);
    showError("Error", "Failed to save call attempt", details, 5000);
  } finally {
    isSubmitting.value = false;
  }
};


// Notification helper
const showNotificationMessage = (message) => {
  notificationMessage.value = message;
  showNotification.value = true;
  setTimeout(() => {
    showNotification.value = false;
  }, 3000);
};

// Load ticket on mount
onMounted(() => {
  fetchTicket();
});
</script>

<style scoped>
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slide-up {
  animation: slideUp 0.3s ease-out;
}
</style>